import { Link } from "react-router";

import { Button } from "~/components/ui/button";
import { cn } from "~/lib/ui";

import { useIntakeEntryHref, useStartIntake } from "../use-start-intake";

export interface TelehealthGetStartedButtonProps {
  /** Treatment slug to start (e.g. "weight-loss"). */
  treatment: string;
  /** Non-PHI source-page/surface id, preserved to intake (`src`). */
  source: string;
  /** Non-PHI CTA position, preserved to intake (`cta`). */
  ctaLocation: string;
  label?: string;
  className?: string;
  /**
   * Builder `noWrap` attributes, spread on the root when this renders as a
   * registered block's output (see CLAUDE.md rule 4).
   */
  attributes?: Record<string, unknown>;
  /**
   * Per-surface test id. No default on purpose (docs/test-ids.md): this
   * primitive is shared, and the engagement-stall modal's own Get Started can
   * sit on top of a PDP — a baked-in id would produce two matching nodes.
   */
  "data-testid"?: string;
}

/**
 * ET-216 — the telehealth "Get Started" CTA. A thin wrapper over the shared
 * ET-12 {@link useStartIntake} contract so every entry surface (landing page,
 * First-Question block, PDP) gates and navigates identically.
 *
 * Renders NOTHING for an unavailable or unknown treatment. On the PDP that is
 * deliberate and load-bearing: these are prescription products with no
 * Add-to-Cart fallback, so an unresolvable treatment must leave the shopper
 * with no purchase affordance rather than one that skips intake.
 *
 * Rendered as a link (via `asChild`) rather than a bare button: this is the ONLY
 * conversion path on a treatment PDP, so a click during the hydration window
 * must still navigate. Hydrated clicks are intercepted so the ET-12 side effects
 * (engagement-stall suppression, client routing) still run — RR's `Link` honours
 * `defaultPrevented`, so the interception is a no-op for it. `prefetch="intent"`
 * warms the intake route + loader on hover (docs/rr7-rules.md).
 *
 * Styling mirrors the PDP's own Add-to-Cart button (`variant="cta"` +
 * `h-[54px]`) so the treatment CTA sits in the same visual slot at the same
 * weight as the button it replaces.
 */
export function TelehealthGetStartedButton({
  treatment,
  source,
  ctaLocation,
  label = "Get Started",
  className,
  attributes,
  "data-testid": dataTestId,
}: TelehealthGetStartedButtonProps) {
  const startIntake = useStartIntake();
  const href = useIntakeEntryHref({ treatment, source, ctaLocation });

  if (!href) return null;

  return (
    <Button
      asChild
      {...attributes}
      variant="cta"
      // `attributes.className` is folded INTO `cn()` rather than left to the
      // spread: a later `className` prop wins outright in JSX, so spreading
      // first and then setting `className` would silently drop whatever Builder
      // injected — its responsive-visibility classes among them, which are
      // functional (`display: none`), not cosmetic. Same hazard `how-it-works`
      // handles for `attributes.style`. The caller's own `className` stays last
      // so it still wins a genuine conflict via twMerge.
      className={cn(
        "min-[768px]:w-[200px] h-[54px] w-full rounded-[.5rem] text-lg font-medium leading-tight",
        (attributes as { className?: string } | undefined)?.className,
        className,
      )}
    >
      <Link
        to={href}
        prefetch="intent"
        onClick={event => {
          // Let modified clicks (new tab / window) behave natively.
          if (
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            event.button !== 0
          ) {
            return;
          }
          event.preventDefault();
          startIntake({ treatment, source, ctaLocation });
        }}
        data-testid={dataTestId}
      >
        {label}
      </Link>
    </Button>
  );
}
